import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import PannelBody from "../../../fragments/PannelBody"
import Button from "../../../common/Button"
import MultipleCheckboxCell from "../../../common/MultipleCheckboxCell"
import CustomSelect from "../../../common/interactive/CustomSelect"
import InputBox from "../../../common/interactive/InputBox"
import FormTextareaBox from "../../../common/interactive/FormTextareaBox"
import {
    addManualTransportationRide,
    getTransportationManualAppointments,
    previewManualTransportationRide,
} from "../../../../lib/fetchApi"
import { practicesLocationsGroupedOptions } from "../../../../lib/systemData"
import { addZero } from "../../../../lib/script"
import { addressesLookTheSame, formatPatientHomeAddress } from "../../../../lib/rideAddressMatch"
import { formatPatientName } from "../../../../lib/patientName"
import { miscellaneous } from "../../../../lib/misc"
import { notificationActions } from "../../../../store/notifications"
import { caseActions } from "../../../../store/case"

import classes from '../forms/Create.module.css'

const formatDateForAppointmentOption = (value) => {
    if (!value) {
        return "No date"
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return `${addZero(value.getMonth() + 1)}/${addZero(value.getDate())}/${value.getFullYear()}`
    }
    const str = String(value)
    const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (dateOnly) {
        return `${dateOnly[2]}/${dateOnly[3]}/${dateOnly[1]}`
    }
    const parsed = new Date(str)
    if (!Number.isNaN(parsed.getTime())) {
        return `${addZero(parsed.getMonth() + 1)}/${addZero(parsed.getDate())}/${parsed.getFullYear()}`
    }
    return str
}

const parseAppointmentDateTime = (appointment) => {
    if (!appointment?.date) {
        return null
    }
    const dateStr = String(appointment.date).slice(0, 10)
    let timeStr = appointment.begin_time ? String(appointment.begin_time) : "00:00:00"
    if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
        timeStr = `${timeStr}:00`
    }
    const parsed = new Date(`${dateStr}T${timeStr}`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

const ManualRideEntry = ({ closeModal }) => {
    const dispatch = useDispatch()
    const userToken = useSelector((state) => state.user.token)
    const activeCase = useSelector((state) => state.case.activeCase)
    const caseId = activeCase?.case?.id

    const [appointments, setAppointments] = useState([])
    const [appointmentsLoading, setAppointmentsLoading] = useState(false)
    const [selectedAppointmentId, setSelectedAppointmentId] = useState(null)
    const [lyftRideId, setLyftRideId] = useState("")
    const [shareRide, setShareRide] = useState([])
    const [relatedPatients, setRelatedPatients] = useState(null)
    const [preview, setPreview] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [addressOverrideReason, setAddressOverrideReason] = useState("")
    const [addressOverrideReasonError, setAddressOverrideReasonError] = useState(null)
    const [fullLocations] = useState(practicesLocationsGroupedOptions)

    const ADDRESS_OVERRIDE_REASON_MIN = 20

    const getAppointmentLocationId = useCallback((appointment) => {
        if (!appointment) {
            return null
        }
        const directCandidates = [
            appointment.location_id,
            appointment.practice_location_id,
            appointment.case_practice_location_id,
            appointment?.location?.id,
            appointment?.practice_location?.id,
            appointment?.practice?.location_id,
            appointment?.practice?.default_location_id,
        ]
        const pickedDirect = directCandidates.find((item) => item != null && item !== "")
        if (pickedDirect != null) {
            return Number(pickedDirect)
        }

        const appointmentPracticeId =
            appointment.practice_id ||
            appointment?.practice?.id ||
            appointment.case_practice_id ||
            appointment?.case_practice?.id
        if (!appointmentPracticeId || !Array.isArray(activeCase?.practices)) {
            return null
        }

        const matchedCasePractice = activeCase.practices.find((item) => (
            Number(item?.id) === Number(appointmentPracticeId) ||
            Number(item?.practice_id) === Number(appointmentPracticeId)
        ))
        if (matchedCasePractice?.location_id != null && matchedCasePractice?.location_id !== "") {
            return Number(matchedCasePractice.location_id)
        }

        return null
    }, [activeCase?.practices])

    const getPracticeAndLocationFromSettings = useCallback((appointment) => {
        const locationId = getAppointmentLocationId(appointment)
        if (!locationId || !Array.isArray(fullLocations)) {
            return {
                practiceName: "N/A",
                locationCode: "N/A",
            }
        }

        for (const practiceGroup of fullLocations) {
            const matchedLocation = practiceGroup?.options?.find(
                (option) => Number(option?.value) === Number(locationId)
            )
            if (matchedLocation) {
                return {
                    practiceName: practiceGroup?.label || "N/A",
                    locationCode:
                        matchedLocation?.location_code ||
                        matchedLocation?.code ||
                        matchedLocation?.short_code ||
                        "N/A",
                }
            }
        }

        return {
            practiceName: "N/A",
            locationCode: "N/A",
        }
    }, [fullLocations, getAppointmentLocationId])

    const loadAppointments = useCallback(async () => {
        if (!caseId || !userToken) {
            return
        }
        setAppointmentsLoading(true)
        try {
            const response = await getTransportationManualAppointments(userToken, caseId)
            const list = Array.isArray(response) ? response : (response?.data ?? [])
            setAppointments(Array.isArray(list) ? list : [])
        } finally {
            setAppointmentsLoading(false)
        }
    }, [caseId, userToken])

    useEffect(() => {
        loadAppointments()
    }, [loadAppointments])

    useEffect(() => {
        if (activeCase) {
            const arr = [...(activeCase?.related_patients ?? [])]
                .filter((item) => item && item.id != null)
            setRelatedPatients(arr.length ? arr : null)
        }
    }, [activeCase])

    const appointmentSelectOptions = useMemo(() => {
        return appointments
            .filter(Boolean)
            .map((appointment) => {
                const appointmentDateTime = parseAppointmentDateTime(appointment)
                let datePart = formatDateForAppointmentOption(appointment?.date)
                let timePart = appointment?.begin_time
                    ? miscellaneous.convert24to12(String(appointment.begin_time)) || appointment.begin_time
                    : "No time"
                if (appointmentDateTime) {
                    datePart = formatDateForAppointmentOption(appointmentDateTime)
                    timePart = miscellaneous.convert24to12(
                        `${addZero(appointmentDateTime.getHours())}:${addZero(appointmentDateTime.getMinutes())}`
                    )
                }
                const locationFromSettings = getPracticeAndLocationFromSettings(appointment)
                const practiceName = locationFromSettings.practiceName
                const locationCode = locationFromSettings.locationCode
                const keptTag = appointment?.is_kept == 1 ? " | Kept" : ""
                const baseLabel = `${datePart} ${timePart} | ${practiceName} | ${locationCode}${keptTag}`

                return {
                    value: appointment?.id,
                    label: baseLabel,
                    appointment,
                    appointmentDateTime,
                }
            })
            .filter((item) => item.value != null)
            .sort((a, b) => {
                if (!a.appointmentDateTime) return 1
                if (!b.appointmentDateTime) return -1
                return b.appointmentDateTime - a.appointmentDateTime
            })
    }, [appointments, getPracticeAndLocationFromSettings])

    const patientHomeAddress = useMemo(
        () => formatPatientHomeAddress(activeCase?.patient?.contact_information),
        [activeCase?.patient?.contact_information],
    )

    const setAppointmentHandler = (value) => {
        const appointmentId = value?.data?.value ? Number(value.data.value) : null
        setSelectedAppointmentId(appointmentId)
        setPreview(null)
    }

    const lyftRideIdHandler = (value) => {
        setLyftRideId(value?.data?.value ?? "")
        setPreview(null)
    }

    const setRelatedPatientsArray = (arr) => {
        if (arr && arr.idArr) {
            const idArray = []
            arr.idArr.forEach((item) => {
                if (item?.id != null) {
                    idArray.push(item.id)
                }
            })
            setShareRide(idArray)
        }
    }

    const previewHandler = async () => {
        if (!selectedAppointmentId) {
            dispatch(notificationActions.setError({ errorMessage: "Please select an appointment." }))
            return
        }
        const trimmed = lyftRideId.trim()
        if (!trimmed) {
            dispatch(notificationActions.setError({ errorMessage: "Please enter the Lyft Ride ID." }))
            return
        }
        if (!caseId) {
            return
        }

        setLoading(true)
        try {
            const result = await previewManualTransportationRide(userToken, caseId, {
                appointment_id: selectedAppointmentId,
                lyft_ride_id: trimmed,
            }, dispatch)

            // genericFetchBearer unwraps JSON `{ data: {...} }` to the inner object on success
            const previewData = result?.data && (result.data.lyft_ride_id != null || result.data.can_save != null)
                ? result.data
                : result

            if (previewData?.errorMessage) {
                const msg = typeof previewData.errorMessage === "string"
                    ? previewData.errorMessage
                    : (previewData.errorMessage?.errors || previewData.errorMessage?.message || "Unable to fetch ride details.")
                dispatch(notificationActions.setError({
                    errorMessage: typeof msg === "string" ? msg : "Please enter a valid Lyft Ride ID",
                }))
                setPreview(null)
            } else if (previewData && (previewData.lyft_ride_id != null || previewData.from != null || previewData.can_save != null)) {
                setPreview(previewData)
                setAddressOverrideReason("")
                setAddressOverrideReasonError(null)
                if (previewData.already_saved_error) {
                    dispatch(notificationActions.setError({ errorMessage: previewData.already_saved_error }))
                } else if (previewData.address_match_error) {
                    dispatch(notificationActions.setError({ errorMessage: previewData.address_match_error }))
                }
            } else if (previewData?.errors || result?.errors) {
                const errors = previewData?.errors || result.errors
                const msg = typeof errors === "string"
                    ? errors
                    : (errors?.lyft_ride_id?.[0] || "Please enter a valid Lyft Ride ID")
                dispatch(notificationActions.setError({ errorMessage: msg }))
                setPreview(null)
            } else {
                dispatch(notificationActions.setError({ errorMessage: "Unable to fetch ride details. Please try again." }))
                setPreview(null)
            }
        } finally {
            setLoading(false)
        }
    }

    const closePreviewHandler = () => {
        setPreview(null)
        setAddressOverrideReason("")
        setAddressOverrideReasonError(null)
    }

    const previewHomeAddress = preview?.pickup_at === "home"
        ? preview.from
        : preview?.pickup_at === "clinic"
            ? preview.to
            : null

    const needsAddressOverrideReason =
        preview?.can_save
        && preview?.home_matched === false
        && !addressesLookTheSame(previewHomeAddress, patientHomeAddress)

    const saveHandler = async () => {
        if (!preview?.can_save) {
            dispatch(notificationActions.setError({
                errorMessage: preview?.already_saved_error
                    || preview?.address_match_error
                    || "Preview the ride and confirm a practice location match before saving.",
            }))
            return
        }
        if (needsAddressOverrideReason && addressOverrideReason.trim().length < ADDRESS_OVERRIDE_REASON_MIN) {
            setAddressOverrideReasonError(`Please enter a reason of at least ${ADDRESS_OVERRIDE_REASON_MIN} characters.`)
            return
        }
        if (!selectedAppointmentId || !caseId) {
            return
        }

        setSaving(true)
        try {
            const body = {
                appointment_id: selectedAppointmentId,
                lyft_ride_id: (preview.lyft_ride_id || lyftRideId).trim(),
                is_shared: shareRide?.length > 0,
                related_patients: shareRide,
            }
            if (needsAddressOverrideReason) {
                body.address_override_reason = addressOverrideReason.trim()
            }
            const ride = await addManualTransportationRide(userToken, caseId, body, dispatch)

            // genericFetchBearer unwraps `{ data: ride }` to the ride object
            const rideItem = ride?.data && ride.data.id != null ? ride.data : ride

            if (ride?.errorMessage || rideItem?.errorMessage) {
                const err = ride?.errorMessage || rideItem.errorMessage
                const msg = typeof err === "string"
                    ? err
                    : (err?.errors || err?.message || "Unable to save manual ride.")
                dispatch(notificationActions.setError({
                    errorMessage: typeof msg === "string" ? msg : (Array.isArray(msg) ? msg.join(", ") : "Unable to save manual ride."),
                }))
            } else if (rideItem?.errors) {
                const msg = typeof rideItem.errors === "string"
                    ? rideItem.errors
                    : (Array.isArray(rideItem.errors) ? rideItem.errors.join(", ") : "Unable to save manual ride.")
                dispatch(notificationActions.setError({ errorMessage: msg }))
            } else if (rideItem?.id) {
                dispatch(notificationActions.setError({ errorMessage: "success", type: "success" }))
                dispatch(caseActions.pushItemInObjArray({
                    folder: "case_transportation",
                    label: "rides",
                    item: rideItem,
                }))
                setPreview(null)
                closeModal()
            } else {
                dispatch(notificationActions.setError({ errorMessage: "Unable to save manual ride." }))
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <PannelBody padding="large" background="var(--line-transparent)">
                <div className={classes.subtitle}>Patient</div>
                {activeCase?.patient && (
                    <>{formatPatientName(activeCase.patient, { order: 'lastFirst' })}</>
                )}
            </PannelBody>
            <PannelBody>
                <div className={classes.inner_title}>
                    <h3>Appointment</h3>
                </div>
                <CustomSelect
                    label="Select Appointment"
                    name="appointment_id"
                    optionsData={appointmentSelectOptions}
                    preSelectedValue={selectedAppointmentId}
                    action={setAppointmentHandler}
                    isDisabled={appointmentsLoading}
                />
                {appointmentsLoading && (
                    <div className={classes.subtitle} style={{ marginTop: "8px" }}>
                        Loading appointments...
                    </div>
                )}

                <div className={classes.inner_title} style={{ marginTop: "10px" }}>
                    <h3>Lyft Ride</h3>
                </div>
                <InputBox
                    label="Lyft Ride ID"
                    name="lyft_ride_id"
                    currentValue={lyftRideId}
                    action={lyftRideIdHandler}
                />

                {relatedPatients && relatedPatients.length > 0 && (
                    <div className={classes.related_container}>
                        <h3>Share ride</h3>
                        <MultipleCheckboxCell items={relatedPatients} action={setRelatedPatientsArray} defaultValues={shareRide} />
                    </div>
                )}

                <div className={classes.form_footer}>
                    <Button type="button" click={previewHandler} isLoading={loading} width="full">
                        Fetch Ride Details
                    </Button>
                </div>
            </PannelBody>

            {preview && (
                <div className={classes.confirmation_modal}>
                    <div className={classes.confirmation_modal_card}>
                        <div className={classes.confirmation_modal_title}>Confirm Ride</div>
                        <p className={classes.confirmation_modal_text}>
                            <strong>Date:</strong>{" "}
                            {(() => {
                                if (!preview.ride_date) {
                                    return "—"
                                }
                                const dateLabel = formatDateForAppointmentOption(preview.ride_date)
                                if (!preview.ride_time) {
                                    return dateLabel
                                }
                                const timeLabel = miscellaneous.convert24to12(String(preview.ride_time))
                                    || preview.ride_time
                                return `${dateLabel} ${timeLabel}`
                            })()}
                        </p>
                        <p className={classes.confirmation_modal_text}>
                            <strong>From:</strong> {preview.from || "—"}
                        </p>
                        <p className={classes.confirmation_modal_text}>
                            <strong>To:</strong> {preview.to || "—"}
                        </p>
                        {/* <p className={classes.confirmation_modal_text}>
                            <strong>Status:</strong> {preview.lyft_ride_status || "—"}
                        </p> */}
                        <p className={classes.confirmation_modal_text}>
                            <strong>Clinic:</strong>{" "}
                            {preview.location_name || (preview.location_id ? `#${preview.location_id}` : "Not matched")}
                        </p>
                        <p className={classes.confirmation_modal_text}>
                            <strong>Direction:</strong>{" "}
                            {preview.pickup_at === "home"
                                ? "Home → Clinic (outbound)"
                                : preview.pickup_at === "clinic"
                                    ? "Clinic → Home (return)"
                                    : "Unknown"}
                        </p>
                        {/* <p className={classes.confirmation_modal_text}>
                            <strong>Link to appointment:</strong> {preview.link_appointment ? "Yes" : "No"}
                        </p> */}
                        {preview.price_estimate != null && preview.price_estimate > 0 && (
                            <p className={classes.confirmation_modal_text}>
                                <strong>Lyft price (est.):</strong> ${Number(preview.price_estimate).toFixed(2)}
                            </p>
                        )}
                        
                        
                        {needsAddressOverrideReason && (
                            <div style={{ marginTop: "12px" }}>
                                <p className={classes.confirmation_modal_text}>
                                    LYFT address does not match the patient address, please provide a reason for the address override.
                                </p>
                                <FormTextareaBox
                                    label="Reason"
                                    name="manual_address_override_reason"
                                    value={addressOverrideReason}
                                    onChange={(event) => {
                                        setAddressOverrideReason(event?.target?.value ?? "")
                                        setAddressOverrideReasonError(null)
                                    }}
                                    required={true}
                                    helperText={`At least ${ADDRESS_OVERRIDE_REASON_MIN} characters.`}
                                    errorText={addressOverrideReasonError}
                                />
                            </div>
                        )}
                        

                        {preview.address_match_error && (
                            <div style={{ marginTop: "12px" }}>
                                <p className={classes.confirmation_modal_text} style={{ color: "#d32f2f" }}>
                                    {preview.address_match_error}
                                </p>
                            </div>
                        )}
                        {preview.already_saved_error && (
                            <div style={{ marginTop: "12px" }}>
                                <p className={classes.confirmation_modal_text} style={{ color: "#d32f2f" }}>
                                    {preview.already_saved_error}
                                </p>
                            </div>
                        )}
                        
                        <div className={classes.confirmation_modal_controls}>
                            <Button
                                type="button"
                                click={saveHandler}
                                isLoading={saving}
                                isDisabled={
                                    !preview.can_save
                                    || (needsAddressOverrideReason && addressOverrideReason.trim().length < ADDRESS_OVERRIDE_REASON_MIN)
                                }
                            >
                                Confirm &amp; Save
                            </Button>
                            <Button type="button" btnStyle="btn_danger" click={closePreviewHandler} isDisabled={saving}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ManualRideEntry
